import { logWarn } from '@ptek/client-logger';
import { useCallback, useContext } from 'react';
import { AppContext } from '../../../context/AppContext';
import {
  DIALOG_TYPES,
  TELEHEALTH_INSTRUCTION_ACTIONS,
  TelehealthInstructionActionType,
  useDialogStore,
} from '../../../stores/dialogStore';
import {
  loadUserSettingsPreferences,
  saveUserSettingsPreferences,
} from '../../../utils/userPreferences';

const TELEHEALTH_CAPTURE_FAILURE_MESSAGE =
  'No audio came from the shared tab. Turn on tab audio when you share it, and note that tab audio capture needs Chrome or Edge.';

const NON_TAB_CAPTURE_MESSAGE =
  'Telehealth capture needs a single browser tab. Choose the tab with your call, turn on tab audio, and try again.';

const UNSUPPORTED_TELEHEALTH_MESSAGE =
  'Telehealth capture is not available in this browser. Use Chrome or another Chromium-based browser to start a telehealth session.';

interface TelehealthInstructionResult {
  action: TelehealthInstructionActionType;
  doNotShowAgain?: boolean;
}

export type TelehealthCaptureResult =
  | {
      status: 'captured';
      displayStream: MediaStream;
    }
  | {
      status: 'cancelled';
      displayStream: null;
    }
  | {
      status: 'failed';
      displayStream: null;
      failureMessage: string;
    };

const stopDisplayStream = (displayStream: MediaStream): void => {
  displayStream.getTracks().forEach((track) => track.stop());
};

/**
 * The picker also offers whole screens and windows. A monitor capture can carry
 * audio, so the audio-track check below would wave it through, and it would put
 * every other chart open on that screen into the recording. Only a surface the
 * browser actually names is rejected: a UA that does not report displaySurface
 * is left to the audio-track check rather than blocked outright.
 */
const isNonTabCapture = (displayStream: MediaStream): boolean => {
  const displaySurface = displayStream
    .getVideoTracks()[0]
    ?.getSettings().displaySurface;
  return displaySurface !== undefined && displaySurface !== 'browser';
};

export const supportsTelehealthCapture = (): boolean =>
  typeof navigator !== 'undefined' &&
  typeof navigator.mediaDevices?.getDisplayMedia === 'function';

export const useTelehealthCapture = () => {
  const { user } = useContext(AppContext);
  const { openDialog } = useDialogStore();
  const practitionerId = user?.username || user?.email;

  const requestCapture =
    useCallback(async (): Promise<TelehealthCaptureResult> => {
      if (!supportsTelehealthCapture()) {
        return {
          status: 'failed',
          displayStream: null,
          failureMessage: UNSUPPORTED_TELEHEALTH_MESSAGE,
        };
      }

      const preferences = loadUserSettingsPreferences(practitionerId);
      if (!preferences.telehealthInstructionsDismissed) {
        const instructionResult = await openDialog<TelehealthInstructionResult>(
          DIALOG_TYPES.TELEHEALTH_INSTRUCTIONS
        );

        if (
          instructionResult.action !== TELEHEALTH_INSTRUCTION_ACTIONS.CONTINUE
        ) {
          return {
            status: 'cancelled',
            displayStream: null,
          };
        }

        if (instructionResult.doNotShowAgain) {
          saveUserSettingsPreferences(practitionerId, {
            ...preferences,
            telehealthInstructionsDismissed: true,
          });
        }
      }

      try {
        const displayStream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: true,
        });

        if (isNonTabCapture(displayStream)) {
          stopDisplayStream(displayStream);
          return {
            status: 'failed',
            displayStream: null,
            failureMessage: NON_TAB_CAPTURE_MESSAGE,
          };
        }

        if (displayStream.getAudioTracks().length === 0) {
          stopDisplayStream(displayStream);
          return {
            status: 'failed',
            displayStream: null,
            failureMessage: TELEHEALTH_CAPTURE_FAILURE_MESSAGE,
          };
        }

        return { status: 'captured', displayStream };
      } catch (error) {
        if (
          error instanceof DOMException &&
          (error.name === 'AbortError' || error.name === 'NotAllowedError')
        ) {
          return {
            status: 'cancelled',
            displayStream: null,
          };
        }

        logWarn('useTelehealthCapture', 'Telehealth tab capture did not start');
        return {
          status: 'failed',
          displayStream: null,
          failureMessage: TELEHEALTH_CAPTURE_FAILURE_MESSAGE,
        };
      }
    }, [openDialog, practitionerId]);

  return { requestCapture };
};
